import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { CalendarDays, Plus, Video, MapPin, Clock, Trash2, Pencil, ChevronLeft, ChevronRight, X, Loader2, RefreshCw, Link2, Unlink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PageHeader from "../components/PageHeader";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Fevrier", "Mars", "Avril", "Mai", "Juin", "Juillet", "Aout", "Septembre", "Octobre", "Novembre", "Decembre"];

function getDaysInMonth(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDay; i++) {
    const d = new Date(year, month, -startDay + i + 1);
    days.push({ date: d, currentMonth: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), currentMonth: true });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), currentMonth: false });
  }
  return days;
}

function dateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default function AgendaPage() {
  const { axiosAuth, isAdmin } = useAuth();
  const api = axiosAuth();
  const [meetings, setMeetings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", time_start: "", time_end: "", meeting_type: "visio", description: "", location: "", client_id: "" });
  const [gcalStatus, setGcalStatus] = useState({ connected: false });
  const [gcalEvents, setGcalEvents] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [importing, setImporting] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const days = getDaysInMonth(year, month);
  const today = dateStr(new Date());

  const load = async () => {
    try {
      const r = await api.get("/meetings");
      setMeetings(r.data);
      if (isAdmin) {
        const c = await api.get("/admin/clients");
        setClients(c.data);
      }
      // Load Google Calendar status
      const gs = await api.get("/calendar/status").catch(() => ({ data: { connected: false } }));
      setGcalStatus(gs.data);
      if (gs.data.connected) {
        const ge = await api.get("/calendar/events").catch(() => ({ data: { events: [] } }));
        setGcalEvents(ge.data.events || []);
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const getMeetingsForDate = (d) => {
    const ds = dateStr(d);
    return meetings.filter(m => m.date === ds);
  };

  const getGcalEventsForDate = (d) => {
    const ds = dateStr(d);
    return gcalEvents.filter(ev => (ev.start || "").startsWith(ds));
  };

  const openCreate = (date) => {
    setEditingId(null);
    setForm({ title: "", date: date ? dateStr(date) : dateStr(new Date()), time_start: "09:00", time_end: "10:00", meeting_type: "visio", description: "", location: "", client_id: "" });
    setShowDialog(true);
  };

  const openEdit = (m) => {
    setEditingId(m.meeting_id);
    setForm({ title: m.title, date: m.date, time_start: m.time_start || "", time_end: m.time_end || "", meeting_type: m.meeting_type || "visio", description: m.description || "", location: m.location || "", client_id: m.client_id || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/meetings/${editingId}`, form);
      } else {
        await api.post("/meetings", form);
      }
      setShowDialog(false);
      load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/meetings/${id}`);
    load();
  };

  const connectGoogleCalendar = async () => {
    try {
      const r = await api.get("/calendar/auth-url");
      window.location.href = r.data.auth_url;
    } catch (e) { alert(e.response?.data?.detail || "Erreur connexion Google Calendar"); }
  };

  const disconnectGoogleCalendar = async () => {
    await api.post("/calendar/disconnect");
    setGcalStatus({ connected: false });
    setGcalEvents([]);
  };

  const syncMeetingToGoogle = async (meetingId) => {
    setSyncing(meetingId);
    try {
      await api.post("/calendar/sync-meeting", { meeting_id: meetingId });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Erreur sync"); }
    setSyncing(null);
  };

  const importGoogleEvents = async () => {
    setImporting(true);
    try {
      const r = await api.post("/calendar/import-events");
      alert(r.data.message);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Erreur import"); }
    setImporting(false);
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const selectedMeetings = selectedDate ? getMeetingsForDate(selectedDate) : [];

  const clientName = (cid) => {
    if (!cid) return "";
    const c = clients.find(cl => cl.user_id === cid);
    return c ? (c.name || c.email) : "";
  };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-6xl" data-testid="agenda-page">
      <PageHeader
        icon={<CalendarDays />}
        title="Agenda"
        description="Gérez vos rendez-vous et réunions"
        testId="agenda-header"
        actions={(
          <>
            {isAdmin && (
              gcalStatus.connected ? (
                <>
                  <Button onClick={importGoogleEvents} disabled={importing} variant="outline"
                    className="border-white/10 text-white hover:bg-white/5" data-testid="import-gcal-btn">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Importer Google
                  </Button>
                  <Button onClick={disconnectGoogleCalendar} variant="ghost" className="text-slate-400 hover:text-red-400" data-testid="disconnect-gcal-btn">
                    <Unlink className="w-4 h-4 mr-1" /> Déconnecter
                  </Button>
                </>
              ) : (
                <Button onClick={connectGoogleCalendar} variant="outline"
                  className="border-white/10 text-white hover:bg-white/5" data-testid="connect-gcal-btn">
                  <Link2 className="w-4 h-4 mr-2" /> Connecter Google Calendar
                </Button>
              )
            )}
            <Button onClick={() => openCreate(null)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="create-meeting-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau RDV
            </Button>
          </>
        )}
      />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Calendar Grid */}
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 animate-fade-in-up">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-white/5"><ChevronLeft className="w-5 h-5 text-slate-400" /></button>
            <div className="text-center">
              <h2 className="text-xl font-bold">{MONTHS[month]} {year}</h2>
              <button onClick={goToday} className="text-xs font-medium mt-1 hover:underline" style={{ color: "#f7c935" }}>Aujourd'hui</button>
            </div>
            <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/5"><ChevronRight className="w-5 h-5 text-slate-400" /></button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map(d => <div key={d} className="text-center text-xs font-bold text-slate-500 py-2">{d}</div>)}
          </div>

          {/* Day Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(({ date, currentMonth }, i) => {
              const ds = dateStr(date);
              const isToday = ds === today;
              const dayMeetings = getMeetingsForDate(date);
              const dayGcalEvents = getGcalEventsForDate(date);
              const isSelected = selectedDate && dateStr(selectedDate) === ds;
              const totalEvents = dayMeetings.length + dayGcalEvents.length;

              return (
                <button key={i} onClick={() => setSelectedDate(date)}
                  className={`relative min-h-[72px] p-1.5 rounded-lg border text-left transition-colors ${
                    !currentMonth ? "opacity-30 border-transparent" :
                    isSelected ? "bg-[#f7c935]/10 border-[#f7c935]/40" :
                    isToday ? "bg-[#461d8a]/20 border-[#461d8a]/40" :
                    "border-white/5 hover:border-white/15 hover:bg-white/[0.02]"
                  }`}
                  data-testid={`cal-day-${ds}`}>
                  <span className={`text-xs font-bold ${isToday ? "text-[#f7c935]" : currentMonth ? "text-slate-300" : "text-slate-700"}`}>
                    {date.getDate()}
                  </span>
                  {totalEvents > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {dayMeetings.slice(0, 2).map(m => (
                        <div key={m.meeting_id} className="text-[10px] px-1 py-0.5 rounded truncate font-medium" style={{ background: m.meeting_type === "visio" ? "#3b82f620" : "#10b98120", color: m.meeting_type === "visio" ? "#3b82f6" : "#10b981" }}>
                          {m.time_start} {m.title}
                        </div>
                      ))}
                      {dayMeetings.length < 2 && dayGcalEvents.slice(0, 2 - dayMeetings.length).map(ev => (
                        <div key={ev.google_event_id} className="text-[10px] px-1 py-0.5 rounded truncate font-medium" style={{ background: "#f59e0b20", color: "#f59e0b" }}>
                          {(ev.start || "").slice(11, 16)} {ev.title}
                        </div>
                      ))}
                      {totalEvents > 2 && <div className="text-[10px] text-slate-500 pl-1">+{totalEvents - 2}</div>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Selected Date Events */}
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold">
                {selectedDate ? new Date(selectedDate).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "Selectionnez une date"}
              </h3>
              {selectedDate && (
                <button onClick={() => openCreate(selectedDate)} className="p-1.5 rounded-lg hover:bg-white/5" data-testid="add-meeting-for-date">
                  <Plus className="w-4 h-4" style={{ color: "#f7c935" }} />
                </button>
              )}
            </div>

            {!selectedDate ? (
              <p className="text-sm text-slate-500 py-4">Cliquez sur un jour du calendrier</p>
            ) : selectedMeetings.length === 0 && (!gcalStatus.connected || getGcalEventsForDate(selectedDate).length === 0) ? (
              <p className="text-sm text-slate-500 py-4">Aucun RDV ce jour</p>
            ) : (
              <div className="space-y-3">
                {selectedMeetings.map(m => (
                  <div key={m.meeting_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3 group" data-testid={`meeting-${m.meeting_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2.5">
                        {m.meeting_type === "visio" ? <Video className="w-4 h-4 mt-0.5 text-blue-400" /> : <MapPin className="w-4 h-4 mt-0.5 text-green-400" />}
                        <div>
                          <div className="font-semibold text-sm">{m.title}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" /> {m.time_start}{m.time_end ? ` - ${m.time_end}` : ""}
                          </div>
                          {m.location && <div className="text-xs text-slate-500 mt-0.5">{m.location}</div>}
                          {m.client_id && clientName(m.client_id) && <div className="text-xs mt-0.5" style={{ color: "#f7c935" }}>Client: {clientName(m.client_id)}</div>}
                          {m.description && <div className="text-xs text-slate-400 mt-1">{m.description}</div>}
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {gcalStatus.connected && !m.google_event_id && (
                          <button onClick={() => syncMeetingToGoogle(m.meeting_id)} disabled={syncing === m.meeting_id}
                            className="p-1 rounded hover:bg-white/10" title="Sync Google Calendar" data-testid={`sync-gcal-${m.meeting_id}`}>
                            {syncing === m.meeting_id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" /> : <Link2 className="w-3.5 h-3.5 text-blue-400" />}
                          </button>
                        )}
                        {m.google_event_id && <span className="p-1" title="Synchronise avec Google"><Link2 className="w-3.5 h-3.5 text-green-400" /></span>}
                        <button onClick={() => openEdit(m)} className="p-1 rounded hover:bg-white/10"><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                        <button onClick={() => handleDelete(m.meeting_id)} className="p-1 rounded hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Google Calendar events */}
                {gcalStatus.connected && selectedDate && getGcalEventsForDate(selectedDate).map(ev => (
                  <div key={ev.google_event_id} className="bg-[#0b0811] border border-yellow-500/20 rounded-lg p-3" data-testid={`gcal-event-${ev.google_event_id}`}>
                    <div className="flex items-start gap-2.5">
                      <CalendarDays className="w-4 h-4 mt-0.5 text-yellow-400" />
                      <div>
                        <div className="font-semibold text-sm flex items-center gap-2">
                          {ev.title}
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 font-medium">Google</span>
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" /> {(ev.start || "").slice(11, 16)}{ev.end ? ` - ${(ev.end || "").slice(11, 16)}` : ""}
                        </div>
                        {ev.location && <div className="text-xs text-slate-500 mt-0.5">{ev.location}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Meetings */}
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-5 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
            <h3 className="text-base font-bold mb-3">Prochains RDV</h3>
            {meetings.filter(m => m.date >= today).sort((a, b) => a.date.localeCompare(b.date) || (a.time_start || "").localeCompare(b.time_start || "")).slice(0, 5).length === 0 ? (
              <p className="text-sm text-slate-500 py-2">Aucun RDV a venir</p>
            ) : (
              <div className="space-y-2">
                {meetings.filter(m => m.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5).map(m => (
                  <button key={m.meeting_id} onClick={() => { setSelectedDate(new Date(m.date + "T12:00:00")); setCurrentDate(new Date(m.date + "T12:00:00")); }}
                    className="w-full flex items-center gap-2.5 p-2.5 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/15 text-left transition-colors">
                    {m.meeting_type === "visio" ? <Video className="w-4 h-4 text-blue-400" /> : <MapPin className="w-4 h-4 text-green-400" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{m.title}</div>
                      <div className="text-xs text-slate-500">{new Date(m.date + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} {m.time_start}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Meeting Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">{editingId ? "Modifier le RDV" : "Nouveau RDV"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="meeting-title" placeholder="Titre du RDV" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            {isAdmin && clients.length > 0 && (
              <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white" data-testid="meeting-client-select"><SelectValue placeholder="Client (optionnel)" /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  <SelectItem value="none">Aucun client</SelectItem>
                  {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" data-testid="meeting-date" />
              <Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
              <Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            </div>
            <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
              <SelectTrigger className="bg-[#0b0811] border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#14101c] border-white/10 text-white">
                <SelectItem value="visio"><div className="flex items-center gap-2"><Video className="w-4 h-4 text-blue-400" /> Visio</div></SelectItem>
                <SelectItem value="presentiel"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-green-400" /> Presentiel</div></SelectItem>
              </SelectContent>
            </Select>
            {form.meeting_type === "presentiel" && (
              <Input placeholder="Lieu" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            )}
            <Textarea placeholder="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]" />
            <Button onClick={handleSave} disabled={!form.title || !form.date} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-meeting-btn">
              {editingId ? "Modifier" : "Creer le RDV"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
